import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import "../../css/EventTagInput.css";

const splitEventos = (value, casePrefix) => {
  if (!value) return [];

  const normalizedPrefix = (casePrefix || "").replace(/\s+/g, " ").trim();
  const marker = normalizedPrefix ? `${normalizedPrefix.toLowerCase()} - ` : "";

  const tokens = value
    .toString()
    .split(/[;,\n]+/)
    .map((item) => {
      const clean = item.replace(/\s+/g, " ").trim();
      if (!marker) return clean;

      const lower = clean.toLowerCase();
      if (lower.startsWith(marker)) {
        return clean.slice(marker.length).trim();
      }

      return clean;
    })
    .filter(Boolean);

  const unique = [];
  const seen = new Set();

  tokens.forEach((item) => {
    const key = item.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(item);
    }
  });

  return unique;
};

const EventTagInput = ({
  value,
  onChange,
  caseDisplayName,
  placeholder,
  maxSuggestions = 8,
  compact = false,
  showHint = true,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastAddedTag, setLastAddedTag] = useState("");

  const casePrefix = (caseDisplayName || "").replace(/\s+/g, " ").trim();
  const tags = useMemo(() => splitEventos(value, casePrefix), [value, casePrefix]);

  const selectedKeys = useMemo(() => {
    return new Set(tags.map((tag) => tag.toLowerCase()));
  }, [tags]);

  const persistTags = (nextTags) => {
    onChange(nextTags.join("; "));
  };

  const addTag = (rawText) => {
    const clean = (rawText || "").replace(/\s+/g, " ").trim();
    if (!clean) return;

    const finalTag = clean;
    const finalKey = finalTag.toLowerCase();

    if (selectedKeys.has(finalKey)) {
      setInputValue("");
      return;
    }

    persistTags([...tags, finalTag]);
    setLastAddedTag(finalTag);
    setInputValue("");
  };

  useEffect(() => {
    if (!lastAddedTag) return;
    const timer = setTimeout(() => setLastAddedTag(""), 1200);
    return () => clearTimeout(timer);
  }, [lastAddedTag]);

  const removeTag = (tagToRemove) => {
    const nextTags = tags.filter((tag) => tag !== tagToRemove);
    persistTags(nextTags);
  };

  const fetchSuggestions = async (query) => {
    setIsLoading(true);
    try {
      const response = await api.get("/eventos/sugerencias", {
        params: {
          q: query || "",
          limit: maxSuggestions,
        },
      });

      const incoming = Array.isArray(response.data) ? response.data : [];
      const filtered = incoming.filter(
        (item) => !selectedKeys.has(item.toLowerCase())
      );

      setSuggestions(filtered);
    } catch (error) {
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const query = inputValue.trim();
    const timer = setTimeout(() => {
      fetchSuggestions(query);
    }, 180);

    return () => clearTimeout(timer);
  }, [inputValue, isOpen]);

  return (
    <div className={`event-tag-input ${compact ? "compact" : ""}`}>
      <div className={`event-tag-shell ${isOpen ? "is-open" : ""}`}>
        <div className="event-tag-list">
          {tags.map((tag) => (
            <span
              className={`event-chip ${lastAddedTag === tag ? "event-chip-selected" : ""}`}
              key={tag}
            >
              <span className="event-chip-text">{tag}</span>
              <button
                type="button"
                className="event-chip-remove"
                onClick={() => removeTag(tag)}
                aria-label={`Quitar evento ${tag}`}
              >
                x
              </button>
            </span>
          ))}

          <input
            type="text"
            className="event-tag-text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setTimeout(() => setIsOpen(false), 120)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
                e.preventDefault();
                addTag(inputValue);
              }
            }}
            placeholder={
              placeholder || "Describa el evento y presione Enter"
            }
          />
        </div>

        <button
          type="button"
          className="event-add-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => addTag(inputValue)}
          aria-label="Agregar evento"
        >
          +
        </button>
      </div>

      {casePrefix && showHint && (
        <p className="event-hint">Caso actual: {casePrefix}. El evento se guarda sin prefijo.</p>
      )}

      {isOpen && (isLoading || suggestions.length > 0) && (
        <div className="event-suggestion-popover">
          {!!inputValue.trim() && (
            <button
              type="button"
              className="event-suggestion-item event-suggestion-create"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addTag(inputValue)}
            >
              <span className="create-icon">+</span>
              <span>Crear evento nuevo: {inputValue.trim()}</span>
            </button>
          )}

          {isLoading && <div className="event-suggestion-item muted">Buscando...</div>}

          {!isLoading &&
            suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className="event-suggestion-item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addTag(suggestion)}
              >
                {suggestion}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default EventTagInput;
